/* Homepage video hero (sections/video-hero.liquid) - playback reliability.
   The video starts with the native muted + playsinline + autoplay
   attributes. Browsers pause it on their own (app / tab switch, back-forward
   cache, power saving, a buffering stall) and nothing used to restart it.
   This only RESUMES, within autoplay policy:
   - one play() attempt when the visible video is paused while the hero is
     on screen and the page is visible (pause event, back on screen, tab /
     app visible again, pageshow incl. back-forward cache);
   - foreground stall (iOS): back from another app WebKit can report the
     video as playing (paused === false) while frames no longer advance. On
     visibilitychange / pageshow / focus the currentTime is sampled; if it
     has not moved 600ms later (hero on screen, page visible) ONE soft
     pause() + play() - no reload, no seek;
   - at most 3 attempts per 10s (shared by both), the Promise rejection is
     handled;
   - poster first: the static poster / preview layer shows until the video
     fires a real "playing" (.is-waiting, rendered in the HTML, hides the
     video layer);
   - never started: no "playing" within START_TIMEOUT (counted only while
     the hero is on screen and the page visible - slow / unreliable
     connection, endless buffering) or a play() refused with NotAllowedError
     (Low Power Mode, data saver, browser setting) = static hero for this
     page view: .is-static hides the video (Safari's play glyph can never
     show), its download is stopped (frees the connection for the rest of
     the page) and nothing is retried. A video that HAS played once and
     later stalls is never made static - the recovery above handles it;
   - prefers-reduced-motion: never plays; the video is stopped and its
     download aborted (the section shows the poster / preview image). */
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var START_TIMEOUT = 2500;

  // Stop for good: no autoplay, download aborted.
  function unload(video) {
    video.removeAttribute('autoplay');
    video.pause();
    video.preload = 'none';
    Array.prototype.forEach.call(video.querySelectorAll('source'), function (source) {
      source.remove();
    });
    video.removeAttribute('src');
    video.load();
  }

  function init(root) {
    if (!root || root.dataset.videoHeroReady) return;
    root.dataset.videoHeroReady = 'true';
    var videos = Array.prototype.slice.call(root.querySelectorAll('video.video-hero__video'));
    if (!videos.length) return;

    if (reducedMotion.matches) {
      videos.forEach(unload);
      return;
    }

    var inView = true;
    var blocked = false;
    var hasPlayed = false;
    var attempts = [];
    var foregroundTimer = null;
    var startTimer = null;

    // The one actually displayed (desktop / mobile swap via CSS).
    function activeVideo() {
      for (var i = 0; i < videos.length; i++) {
        if (videos[i].getClientRects().length) return videos[i];
      }
      return null;
    }

    function canAttempt() {
      var now = Date.now();
      attempts = attempts.filter(function (time) {
        return now - time < 10000;
      });
      if (attempts.length >= 3) return false;
      attempts.push(now);
      return true;
    }

    function goStatic() {
      if (blocked) return;
      blocked = true;
      window.clearTimeout(startTimer);
      window.clearTimeout(foregroundTimer);
      root.classList.remove('is-waiting');
      root.classList.add('is-static');
      videos.forEach(unload);
    }

    // Never started: one countdown while the hero can actually be seen;
    // hidden tab / scrolled away when it ends = a fresh one once visible.
    function armStartTimeout() {
      if (hasPlayed || blocked || startTimer) return;
      startTimer = window.setTimeout(function () {
        startTimer = null;
        if (hasPlayed || blocked) return;
        if (inView && !document.hidden) goStatic();
      }, START_TIMEOUT);
    }

    function play(video) {
      video.muted = true;
      var result;
      try {
        result = video.play();
      } catch (error) {
        return;
      }
      if (result && typeof result.catch === 'function') {
        result.catch(function (error) {
          if (error && error.name === 'NotAllowedError') goStatic();
        });
      }
    }

    function ready() {
      return !blocked && inView && !document.hidden && !reducedMotion.matches;
    }

    function resume() {
      var video = activeVideo();
      if (!video || !video.paused || video.ended || !ready() || !canAttempt()) return;
      play(video);
    }

    // Back in the foreground: a video that claims to play but does not move
    // gets one soft restart.
    function foregroundCheck() {
      var video = activeVideo();
      if (!video || !hasPlayed || blocked || reducedMotion.matches) return;
      var start = video.currentTime;
      window.clearTimeout(foregroundTimer);
      foregroundTimer = window.setTimeout(function () {
        if (activeVideo() !== video || !ready()) return;
        if (video.paused) {
          resume();
          return;
        }
        // Moving, or simply still buffering (not enough data) = leave it.
        if (Math.abs(video.currentTime - start) > 0.01 || video.readyState < 3 || !canAttempt()) return;
        video.pause();
        play(video);
      }, 600);
    }

    // Poster first: the video layer appears only once it really plays.
    root.classList.add('is-waiting');
    function markPlaying(video) {
      if (blocked || video !== activeVideo()) return;
      hasPlayed = true;
      window.clearTimeout(startTimer);
      root.classList.remove('is-waiting');
    }
    videos.forEach(function (video) {
      video.addEventListener('playing', function () {
        markPlaying(video);
      });
      if (!video.paused && video.readyState >= 3) markPlaying(video);
    });

    videos.forEach(function (video) {
      video.addEventListener('pause', function () {
        window.setTimeout(resume, 300);
      });
      // Some in-app browsers ignore the autoplay attribute but allow a muted play().
      video.addEventListener('canplay', resume, { once: true });
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        if (inView) {
          resume();
          armStartTimeout();
        }
      }).observe(root);
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        resume();
        foregroundCheck();
        armStartTimeout();
      }
    });
    window.addEventListener('pageshow', function (event) {
      resume();
      if (event.persisted) foregroundCheck();
    });
    window.addEventListener('focus', foregroundCheck);
    // No autoplay after load (e.g. Low Power Mode never starts it): one
    // explicit attempt - its rejection is the signal to go static.
    resume();
    armStartTimeout();
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.video-hero'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
